import { ReactNode } from 'react';
import { useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Menu from '@/design_system/Menu';
import { MenuItem } from '@/design_system/Menu/Menu';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import IconAfterSales from '@/icons/AfterSales.svg';
import IconBuilding from '@/icons/Building.svg';
import IconChevron from '@/icons/Chevron.svg';
import IconPlace from '@/icons/Place.svg';
import IconStore from '@/icons/Store.svg';
import IconWorkshop from '@/icons/Workshop.svg';
import IconWorld from '@/icons/World.svg';
import { hasRoleOnEntity, hasRoleOnOrganization } from '@/models/user';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { useShipmentContext } from '@/routes/Shipments/Shipment/ShipmentContext';
import { useCurrentSession } from '@/services/auth';
import useViewPort from '@/utils/useViewport';

import { IComment } from '../Comments/Comments';

const useVisibilityOptions = (): {
  organization: { id: string; name: string } | null;
  entities: {
    id: string;
    name: string;
    reason: 'origin' | 'destination' | 'store' | 'afterSales' | 'workshop';
  }[];
} => {
  const { currentSession } = useCurrentSession();

  const { request } = useRequestContext();
  const { shipment } = useShipmentContext();

  let organization = null;
  const entities = [];

  if (shipment) {
    if (shipment.organization && hasRoleOnOrganization(currentSession, shipment.organization.id)) {
      organization = shipment.organization;
    }
    if (shipment.originEntity && hasRoleOnEntity(currentSession, shipment.originEntity.id)) {
      entities.push({
        id: shipment.originEntity.id,
        name: shipment.originEntity.name,
        reason: 'origin' as const,
      });
    }
    if (
      shipment.destinationEntity &&
      hasRoleOnEntity(currentSession, shipment.destinationEntity.id)
    ) {
      entities.push({
        id: shipment.destinationEntity.id,
        name: shipment.destinationEntity.name,
        reason: 'destination' as const,
      });
    }
  } else if (request) {
    if (request.organization && hasRoleOnOrganization(currentSession, request.organization.id)) {
      organization = request.organization;
    }
    if (request.store && hasRoleOnEntity(currentSession, request.store.id)) {
      entities.push({
        id: request.store.id,
        name: request.store.name,
        reason: 'store' as const,
      });
    }
    if (request.afterSales && hasRoleOnEntity(currentSession, request.afterSales.id)) {
      entities.push({
        id: request.afterSales.id,
        name: request.afterSales.name,
        reason: 'afterSales' as const,
      });
    }
    request.allArticles.forEach((article) => {
      if (article.workshop && hasRoleOnEntity(currentSession, article.workshop.id)) {
        entities.push({
          id: article.workshop.id,
          name: article.workshop.name,
          reason: 'workshop' as const,
        });
      }
    });
  }

  return {
    organization,
    entities,
  };
};

export const VisibilitySelector = ({
  visibility,
  setVisibility,
  entityId,
  setEntityId,
}: {
  visibility: IComment['visibility'];
  setVisibility: (visibility: IComment['visibility']) => void;
  entityId: string | null;
  setEntityId: (entityId: string | null) => void;
}) => {
  const { t } = useLingui();
  const { isMobile } = useViewPort();

  const { organization, entities } = useVisibilityOptions();

  const getVisibilityLabel = ({
    visibility,
    organization,
    entity,
  }: {
    visibility: IComment['visibility'];
    organization?: { id: string; name: string };
    entity?: { id: string; name: string };
  }): string | undefined => {
    switch (visibility) {
      case 'public':
        return t({
          id: 'new-comment.visibility.public',
          message: 'Visible by everyone with access to this page',
        });
      case 'organization':
        return t({
          id: 'new-comment.visibility.organization',
          message: `Only ${organization?.name} users can see`,
        });
      case 'entity':
        return t({
          id: 'new-comment.visibility.entity',
          message: `Only ${entity?.name} users can see`,
        });
      case 'client':
        return;
    }
  };

  const getVisibilityIcon = ({
    visibility,
    entity,
  }: {
    visibility: IComment['visibility'];
    entity?: {
      id: string;
      name: string;
      reason: 'origin' | 'destination' | 'store' | 'afterSales' | 'workshop';
    };
  }): ReactNode => {
    switch (visibility) {
      case 'public':
        return <IconWorld style={{ fontSize: '1.25rem' }} />;
      case 'organization':
        return <IconBuilding style={{ fontSize: '1.25rem' }} />;
      case 'entity':
        switch (entity?.reason) {
          case 'origin':
            return <IconPlace start style={{ fontSize: '1.25rem' }} />;
          case 'destination':
            return <IconPlace end style={{ fontSize: '1.25rem' }} />;
          case 'store':
            return <IconStore style={{ fontSize: '1.25rem' }} />;
          case 'afterSales':
            return <IconAfterSales style={{ fontSize: '1.25rem' }} />;
          case 'workshop':
            return <IconWorkshop style={{ fontSize: '1.25rem' }} />;
        }
        break;
    }
  };

  const VisibilityMenuItem = ({
    id,
    visibility,
    organization,
    entity,
  }: {
    id: string;
    visibility: IComment['visibility'];
    organization?: { id: string; name: string };
    entity?: {
      id: string;
      name: string;
      reason: 'origin' | 'destination' | 'store' | 'afterSales' | 'workshop';
    };
  }) => {
    return (
      <MenuItem id={id}>
        <Stack row gap="0.25rem" alignItems="center" flexWrap="nowrap">
          {getVisibilityIcon({ visibility, entity })}
          {getVisibilityLabel({ visibility, organization, entity })}
        </Stack>
      </MenuItem>
    );
  };

  // eslint-disable-next-line react/prop-types
  const selectedEntity = entities.find((entity) => entity.id === entityId);

  return (
    <Menu
      trigger={
        <Button
          style={{ textAlign: 'justify', padding: '0.5rem' }}
          variant="secondary"
          size="small"
          ariaLabel={t({ id: 'new-comment.visibility', message: 'Visibility' })}
        >
          <Stack row gap="0.25rem" alignItems="center" flexWrap="nowrap">
            {getVisibilityIcon({
              visibility,
              entity: selectedEntity,
            })}
            {!isMobile && (
              <InlineText size="200">
                {getVisibilityLabel({
                  visibility,
                  organization: organization ?? undefined,
                  entity: selectedEntity,
                })}
              </InlineText>
            )}
            <IconChevron down style={{ fontSize: '1.25rem' }} />
          </Stack>
        </Button>
      }
      onAction={(id) => {
        if (['public', 'organization'].includes(id)) {
          setVisibility(id as IComment['visibility']);
          setEntityId(null);
        } else {
          setVisibility('entity');
          setEntityId(id);
        }
      }}
    >
      <VisibilityMenuItem id="public" visibility="public" />
      {organization && (
        <VisibilityMenuItem
          id="organization"
          visibility="organization"
          organization={organization}
        />
      )}
      {entities.filter(Boolean).map((entity) => (
        // eslint-disable-next-line react/prop-types
        <VisibilityMenuItem id={entity.id} key={entity.id} visibility="entity" entity={entity} />
      ))}
    </Menu>
  );
};
